import { Link } from 'react-router-dom';
import { APP_NAME } from '@teentack/shared';
import { Logo } from '@/components/Logo';
import { FOOTER_COLUMNS, SUPPORT_EMAIL, SUPPORT_PHONE } from '@/content/site-content';

/** The two "coming soon" store badges. Neither links anywhere yet. */
function StoreBadge({ store }: { store: 'google' | 'apple' }) {
  return (
    <span className="inline-flex items-center gap-2.5 rounded-lg border border-white/15 bg-black px-3 py-2">
      <svg viewBox="0 0 24 24" className="size-5 shrink-0" aria-hidden="true" focusable="false">
        {store === 'google' ? (
          <path
            d="M3.6 2.3 14 12 3.6 21.7a1.6 1.6 0 0 1-.6-1.3V3.6c0-.5.2-1 .6-1.3m11.7 10.6 2.9 2.7-3.3 1.9-2.7-2.7zm0-1.8L12.2 8l3.3-1.9 3.3 1.9zm4.4 3.1-2.6-2.2 2.6-2.2c1.2.7 1.8 1.3 1.8 2.2s-.6 1.5-1.8 2.2"
            fill="currentColor"
          />
        ) : (
          <path
            d="M16.1 12.7c0-2.2 1.8-3.2 1.9-3.3-1-1.5-2.6-1.7-3.2-1.8-1.4-.1-2.7.8-3.3.8s-1.7-.8-2.8-.8c-1.5 0-2.8.8-3.6 2.1-1.5 2.6-.4 6.5 1.1 8.6.7 1 1.6 2.2 2.7 2.1 1.1 0 1.5-.7 2.8-.7s1.6.7 2.8.7c1.1 0 1.9-1 2.6-2.1.8-1.2 1.2-2.4 1.2-2.4s-2.2-.9-2.2-3.2M14 6.2c.6-.7 1-1.7.9-2.7-.9 0-1.9.6-2.5 1.3-.6.6-1 1.6-.9 2.6 1 .1 2-.5 2.5-1.2"
            fill="currentColor"
          />
        )}
      </svg>
      <span className="flex flex-col leading-none">
        <span className="text-[0.55rem] text-white/70">Coming Soon</span>
        <span className="mt-0.5 text-sm font-semibold text-white">
          {store === 'google' ? 'Google Play' : 'App Store'}
        </span>
      </span>
    </span>
  );
}

export function SiteFooter() {
  return (
    <footer className="mt-4 bg-ink text-white/70">
      <div className="container-page py-14">
        <div className="grid gap-10 lg:grid-cols-[1.6fr_1fr_1fr_1fr_1.2fr]">
          <div>
            <Logo variant="light" />

            <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/60">
              {APP_NAME} is a safe platform that connects teens, young adults, and young
              entrepreneurs with real opportunities to learn, earn, and grow with trusted support
              every step of the way.
            </p>

            <div className="mt-5 flex flex-wrap gap-3">
              <StoreBadge store="google" />
              <StoreBadge store="apple" />
            </div>
          </div>

          {FOOTER_COLUMNS.map((column) => (
            <nav key={column.heading} aria-label={column.heading}>
              <h2 className="text-xs font-extrabold tracking-[0.1em] text-white uppercase">
                {column.heading}
              </h2>
              <ul className="mt-4 space-y-2.5">
                {column.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      className="text-sm text-white/65 transition duration-200 hover:text-white"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>

              {column.heading === 'Support' && (
                <ul className="mt-4 space-y-2.5 text-sm text-white/65">
                  <li>
                    <a
                      href={`mailto:${SUPPORT_EMAIL}`}
                      className="transition duration-200 hover:text-white"
                    >
                      {SUPPORT_EMAIL}
                    </a>
                  </li>
                  <li>{SUPPORT_PHONE}</li>
                </ul>
              )}
            </nav>
          ))}

          <div>
            <h2 className="text-xs font-extrabold tracking-[0.1em] text-white uppercase">
              Stay Updated
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-white/60">
              Get tips, updates, and new opportunities delivered to you.
            </p>

            {/* No newsletter endpoint exists yet, so this points at the
                early-access form on the landing page rather than posting
                nowhere. [TODO: swap for a real subscribe endpoint] */}
            <Link
              to="/#waitlist"
              className="mt-4 block rounded-lg border border-white/15 bg-white/5 px-3 py-2.5 text-sm text-white/50 transition duration-200 hover:border-white/30"
            >
              Your email address
            </Link>
            <Link
              to="/#waitlist"
              className="mt-2 block rounded-lg bg-brand-600 px-3 py-2 text-center text-sm font-semibold text-white transition duration-200 hover:bg-brand-700"
            >
              Subscribe
            </Link>

            <p className="mt-4 text-xs leading-relaxed text-white/50">
              We respect your privacy. No spam, unsubscribe anytime.
            </p>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-3 border-t border-white/10 pt-6 text-sm text-white/55 sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {new Date().getFullYear()} {APP_NAME}. All rights reserved.
          </p>
          <p>
            Legal entity details in our{' '}
            <Link to="/#safety" className="text-white/75 hover:text-white">
              Terms of Service
            </Link>{' '}
            - mailing address in our{' '}
            <Link to="/#safety" className="text-white/75 hover:text-white">
              Privacy Policy
            </Link>
            .
          </p>
        </div>

        <p className="mt-4 text-sm text-white/55">
          Developed by{' '}
          <span className="font-semibold text-white/80">FAcode Software Solutions Pvt Ltd</span>
        </p>
      </div>
    </footer>
  );
}
